// src/pages/AdminAgencias.jsx
import { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase/firebase.js";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
} from "firebase/firestore";

export default function AdminAgencias() {
  // Estado del usuario (para mostrar en UI y evitar warning)
  const [me, setMe] = useState(null);

  // Listado y selección
  const [agencias, setAgencias] = useState([]);
  const [selId, setSelId] = useState(""); // id del doc seleccionado (o vacío para "nueva")

  // Formulario
  const [form, setForm] = useState({
    agenciaId: "",
    nombre: "",
    activa: true,
    limitePublicaciones: 0,
    usadas: 0,
    direccion: {
      calle: "",
      ciudad: "",
      estado: "",
      cp: "",
      pais: "México",
      full: "",
    },
    contacto: {
      email: "",
    },
    telefonos: [],
    whatsapps: [],
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Cargar usuario actual (solo para mostrar algo en UI y evitar el warning)
  useEffect(() => {
    const u = auth.currentUser;
    if (u) {
      setMe({ uid: u.uid, email: u.email || "" });
    } else {
      setMe(null);
    }
  }, []);

  // Cargar listado de agencias
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, "agencias"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAgencias(list.sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")));

        // Si hay alguna, preselecciona la primera
        if (list.length > 0) {
          const first = list[0];
          setSelId(first.id);
          setForm({
            agenciaId: first.agenciaId || first.id || "",
            nombre: first.nombre || "",
            activa: !!first.activa,
            limitePublicaciones: Number(first.limitePublicaciones) || 0,
            usadas: Number(first.usadas) || 0,
            direccion: {
              calle: first?.direccion?.calle || "",
              ciudad: first?.direccion?.ciudad || "",
              estado: first?.direccion?.estado || "",
              cp: first?.direccion?.cp || "",
              pais: first?.direccion?.pais || "México",
              full: first?.direccion?.full || "",
            },
            contacto: {
              email: first?.contacto?.email || "",
            },
            telefonos: Array.isArray(first?.telefonos) ? first.telefonos : [],
            whatsapps: Array.isArray(first?.whatsapps) ? first.whatsapps : [],
          });
        } else {
          // Si no hay agencias, deja el formulario listo para crear
          setSelId("");
          setForm((v) => ({
            ...v,
            agenciaId: "",
            nombre: "",
          }));
        }
      } catch (e) {
        console.error("[AdminAgencias] cargar agencias:", e);
        alert("No se pudo cargar el listado de agencias.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // no pongas `db` aquí; no cambia

  // Cuando cambia la selección, carga esa agencia al form
  useEffect(() => {
    if (!selId) return; // modo "nueva"
    const a = agencias.find((x) => x.id === selId);
    if (!a) return;
    setForm({
      agenciaId: a.agenciaId || a.id || "",
      nombre: a.nombre || "",
      activa: !!a.activa,
      limitePublicaciones: Number(a.limitePublicaciones) || 0,
      usadas: Number(a.usadas) || 0,
      direccion: {
        calle: a?.direccion?.calle || "",
        ciudad: a?.direccion?.ciudad || "",
        estado: a?.direccion?.estado || "",
        cp: a?.direccion?.cp || "",
        pais: a?.direccion?.pais || "México",
        full: a?.direccion?.full || "",
      },
      contacto: {
        email: a?.contacto?.email || "",
      },
      telefonos: Array.isArray(a?.telefonos) ? a.telefonos : [],
      whatsapps: Array.isArray(a?.whatsapps) ? a.whatsapps : [],
    });
  }, [selId, agencias]);

  const esNueva = useMemo(() => selId === "", [selId]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("direccion.")) {
      const field = name.split(".")[1];
      setForm((v) => ({ ...v, direccion: { ...v.direccion, [field]: value } }));
      return;
    }
    if (name.startsWith("contacto.")) {
      const field = name.split(".")[1];
      setForm((v) => ({ ...v, contacto: { ...v.contacto, [field]: value } }));
      return;
    }
    setForm((v) => ({
      ...v,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onChangeLista = (name, raw) => {
    // Convierte texto con comas/lineas en array limpio
    const arr = (raw || "")
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    setForm((v) => ({ ...v, [name]: arr }));
  };

  const limpiar = () => {
    setSelId("");
    setForm({
      agenciaId: "",
      nombre: "",
      activa: true,
      limitePublicaciones: 0,
      usadas: 0,
      direccion: {
        calle: "",
        ciudad: "",
        estado: "",
        cp: "",
        pais: "México",
        full: "",
      },
      contacto: { email: "" },
      telefonos: [],
      whatsapps: [],
    });
  };

  const guardar = async () => {
    try {
      setSaving(true);

      const payload = {
        agenciaId: (form.agenciaId || "").trim(),
        nombre: (form.nombre || "").trim(),
        activa: !!form.activa,
        limitePublicaciones: Number(form.limitePublicaciones) || 0,
        usadas: Number(form.usadas) || 0,
        direccion: {
          calle: form.direccion.calle || "",
          ciudad: form.direccion.ciudad || "",
          estado: form.direccion.estado || "",
          cp: form.direccion.cp || "",
          pais: form.direccion.pais || "México",
          full:
            form.direccion.full ||
            [form.direccion.calle, form.direccion.cp, form.direccion.ciudad, form.direccion.estado]
              .filter(Boolean)
              .join(", "),
        },
        contacto: { email: (form.contacto.email || "").trim() },
        telefonos: Array.isArray(form.telefonos) ? form.telefonos : [],
        whatsapps: Array.isArray(form.whatsapps) ? form.whatsapps : [],
        updatedAt: new Date().toISOString(),
      };

      if (!payload.agenciaId) {
        alert("agenciaId es obligatorio.");
        setSaving(false);
        return;
      }

      const docId = esNueva ? payload.agenciaId : selId;
      const ref = doc(db, "agencias", docId);

      if (esNueva) {
        await setDoc(ref, {
          ...payload,
          createdAt: new Date().toISOString(),
        });
      } else {
        await updateDoc(ref, payload);
      }

      alert("Agencia guardada.");
      // recargar listado
      const snap = await getDocs(collection(db, "agencias"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAgencias(list.sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")));
      setSelId(docId);
    } catch (e) {
      console.error("[AdminAgencias] guardar:", e);
      alert(e.message || "No se pudo guardar la agencia.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-600">Cargando…</div>;

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      {/* Topbar */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white flex justify-between items-center">
        <div className="font-semibold">Administrar Agencias</div>
        <div className="text-sm text-gray-600">Admin · {me?.email || ""}</div>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista */}
        <aside className="lg:col-span-1">
          <div className="rounded border border-gray-200 bg-white p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Agencias</h3>
              <button
                onClick={limpiar}
                className="px-2 py-1 rounded bg-gray-900 text-white text-sm hover:bg-gray-800"
              >
                + Nueva
              </button>
            </div>

            <div className="space-y-1 max-h-[60vh] overflow-auto">
              {agencias.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setSelId(a.id)}
                  className={`w-full text-left px-3 py-2 rounded border ${
                    selId === a.id ? "border-gray-900 bg-gray-50" : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="font-medium">{a.nombre || a.id}</div>
                  <div className="text-xs text-gray-500">
                    {a.agenciaId} · {a.usadas || 0}/{a.limitePublicaciones || 0} · {a.activa ? "Activa" : "Inactiva"}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Form */}
        <main className="lg:col-span-2">
          <div className="rounded border border-gray-200 bg-white p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-sm text-gray-600">agenciaId</label>
                <input
                  name="agenciaId"
                  value={form.agenciaId}
                  onChange={onChange}
                  disabled={!esNueva}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  placeholder=""
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm text-gray-600">Nombre</label>
                <input
                  name="nombre"
                  value={form.nombre}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  placeholder=""
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="text-sm text-gray-600">Activa</label>
                <div className="mt-2">
                  <input
                    type="checkbox"
                    name="activa"
                    checked={!!form.activa}
                    onChange={onChange}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600">Límite publicaciones</label>
                <input
                  name="limitePublicaciones"
                  type="number"
                  value={form.limitePublicaciones}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Usadas</label>
                <input
                  name="usadas"
                  type="number"
                  value={form.usadas}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Email contacto</label>
                <input
                  name="contacto.email"
                  type="email"
                  value={form.contacto.email}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  placeholder="tucorreo@gmail.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="md:col-span-3">
                <label className="text-sm text-gray-600">Calle</label>
                <input
                  name="direccion.calle"
                  value={form.direccion.calle}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Ciudad</label>
                <input
                  name="direccion.ciudad"
                  value={form.direccion.ciudad}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Estado</label>
                <input
                  name="direccion.estado"
                  value={form.direccion.estado}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">CP</label>
                <input
                  name="direccion.cp"
                  value={form.direccion.cp}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">País</label>
                <input
                  name="direccion.pais"
                  value={form.direccion.pais}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm text-gray-600">Dirección (full)</label>
                <input
                  name="direccion.full"
                  value={form.direccion.full}
                  onChange={onChange}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                  placeholder="Calle, numero, CP, Ciudad, Estado"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-600">Teléfonos (coma o saltos)</label>
                <textarea
                  rows={3}
                  value={form.telefonos.join("\n")}
                  onChange={(e) => onChangeLista("telefonos", e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">WhatsApps (coma o saltos)</label>
                <textarea
                  rows={3}
                  value={form.whatsapps.join("\n")}
                  onChange={(e) => onChangeLista("whatsapps", e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded border border-gray-300 bg-gray-50"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={guardar}
                disabled={saving}
                className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {saving ? "Guardando…" : esNueva ? "Crear agencia" : "Guardar cambios"}
              </button>
              {!esNueva && (
                <span className="text-sm text-gray-600">
                  ID doc: <code>{selId}</code>
                </span>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}